import React, { ReactNode, useEffect, useRef } from "react"
import { Flex, Box, List } from "@chakra-ui/react"
import _ from "lodash"
import { SIDEBAR_WIDTHS } from "utilities/enums"
import SimpleBar from "simplebar-react"
import PageContent from "./PageContent"
import ScrollToTop from "../Navigation/ScrollToTop"

interface EventResourcesViewProps {
  listWidth?: "xs" | "sm" | "md" | "lg" | "xl"
  listHeader?: ReactNode
  listItems: ReactNode | ReactNode[]
  emptyState?: ReactNode
  selectedItem: ReactNode
  onScrollEnd?: () => void
  onContentScrollEnd?: () => void
  bgColor?: string
  showBorder?: boolean
  scrollViewRef?: React.RefObject<HTMLDivElement>
  contentPadding?: number
  contentMaxWidth?: number | string
}

const EventResourcesView = ({
  listWidth = "lg",
  listHeader,
  listItems,
  emptyState,
  selectedItem,
  onScrollEnd,
  onContentScrollEnd,
  bgColor,
  showBorder = true,
  scrollViewRef,
  contentPadding,
  contentMaxWidth,
}: EventResourcesViewProps) => (
  <Flex flexGrow={1} position="relative" h="100%">
    <Flex
      w={SIDEBAR_WIDTHS[listWidth!]}
      minWidth={listWidth === "xs" ? 200 : 300}
      maxWidth={500}
      borderRightWidth={1}
      direction="column"
      flexShrink={0}
      position="relative"
    >
      {listHeader}
      {emptyState ? (
        <EmptyStateOverlay>{emptyState}</EmptyStateOverlay>
      ) : (
        <ScrollView
          listItems={listItems}
          onScrollEnd={onScrollEnd}
          scrollableNodeRef={scrollViewRef}
        />
      )}
    </Flex>
    <Flex
      direction="column"
      flexGrow={1}
    >
      <SelectedItem
        selectedItem={selectedItem}
        onContentScrollEnd={onContentScrollEnd}
        showBorder={showBorder}
        bgColor={bgColor}
        contentPadding={contentPadding}
        contentMaxWidth={contentMaxWidth}
      />
    </Flex>
  </Flex>
)

const EmptyStateOverlay = ({ children }: { children: ReactNode }) => (
  <Box
    position="absolute"
    inset={0}
    display="flex"
    alignItems="center"
    justifyContent="center"
    pointerEvents="none"
  >
    <Box pointerEvents="auto">{children}</Box>
  </Box>
)

const ScrollView = ({ listItems, onScrollEnd, scrollableNodeRef }) => {
  const handleScroll = (e) => {
    if (e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 10) {
      onScrollEnd!()
    }
  }

  const debouncedHandleScroll = _.debounce(handleScroll, 100)

  return (
    <Box
      as={SimpleBar}
      h={1}
      flexGrow={1}
      scrollableNodeProps={{
        ref: scrollableNodeRef,
        onScroll: (e) => {
          if (onScrollEnd) {
            e.persist()
            debouncedHandleScroll(e)
          }
        },
      }}
    >
      <List>
        {listItems}
      </List>
    </Box>
  )
}

const SelectedItem = ({
  selectedItem, onContentScrollEnd, showBorder, bgColor, contentPadding, contentMaxWidth,
}) => {
  const ref = useRef(null)
  const scrollableNodeRef = useRef(null)

  const handleScroll = (e) => {
    if (e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 10) {
      onContentScrollEnd!()
    }
  }

  const debouncedHandleScroll = _.debounce(handleScroll, 100)

  useEffect(() => {
    if (ref.current) {
      // @ts-ignore
      ref.current.contentEl.style.height = "100%"
    }
  }, [ref])

  return (
    <Box
      position="relative"
      overflowX="hidden"
      as={SimpleBar}
      h={1}
      flexGrow={1}
      ref={ref}
      scrollableNodeProps={{
        onScroll: (e) => {
          if (onContentScrollEnd) {
            e.persist()
            debouncedHandleScroll(e)
          }
        },
        ref: scrollableNodeRef,
      }}
      background={bgColor || (showBorder ? "gray.50" : "white")}
    >
      <PageContent
        showBorder={showBorder}
        padding={contentPadding}
        maxWidth={contentMaxWidth}
      >
        {selectedItem}
      </PageContent>
      <ScrollToTop containerRef={scrollableNodeRef} />
    </Box>
  )
}

export default EventResourcesView
